import { apiEmployeeRecognitions } from "@/api/employees/employee-recognition.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiEmployeeRecognitionFilter,
  ApiEmployeeRecognitionGift,
  ApiEmployeeRecognitionUpdate,
} from "@/types/recognitions/recognition.types.ts";
import { recognitionsKeys } from "@/queries/use-recognitions.query.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const employeeRecognitionsKeys = createQueryKeys("employeeRecognitions", {
  search: (employeeId: number, filter: ApiEmployeeRecognitionFilter) => ({
    queryKey: [employeeId, filter],
    queryFn: async () => {
      const result = await apiEmployeeRecognitions.search(employeeId, filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useEmployeeRecognitionsSearch = (
  employeeId: number,
  filter: ApiEmployeeRecognitionFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeeRecognitionsKeys.search(employeeId, filter),
    enabled: enabled && !!employeeId,
  });
};

const invalidateEmployeeRecognitions = (
  queryClient: ReturnType<typeof useQueryClient>,
  employeeId: number
) => {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: [...employeeRecognitionsKeys.search._def, employeeId],
    }),
    queryClient.invalidateQueries({ queryKey: recognitionsKeys.history._def }),
  ]);
};

interface EmployeeRecognitionGiftParams {
  employeeId: number;
  payload: ApiEmployeeRecognitionGift;
}

export const useEmployeeRecognitionGift = <T extends FieldValues>(
  control: Control<T> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ employeeId, payload }: EmployeeRecognitionGiftParams) => {
      const result = await apiEmployeeRecognitions.gift(employeeId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      return invalidateEmployeeRecognitions(queryClient, variables.employeeId);
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

interface EmployeeRecognitionUpdateParams {
  employeeId: number;
  itemId: number;
  payload: ApiEmployeeRecognitionUpdate;
}

export const useEmployeeRecognitionUpdate = <T extends FieldValues>(
  control: Control<T> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ employeeId, itemId, payload }: EmployeeRecognitionUpdateParams) => {
      const result = await apiEmployeeRecognitions.update(employeeId, itemId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      return invalidateEmployeeRecognitions(queryClient, variables.employeeId);
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

interface EmployeeRecognitionRevokeParams {
  employeeId: number;
  itemId: number;
}

export const useEmployeeRecognitionRevoke = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ employeeId, itemId }: EmployeeRecognitionRevokeParams) => {
      const result = await apiEmployeeRecognitions.revoke(employeeId, itemId);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: (_, variables) => {
      return invalidateEmployeeRecognitions(queryClient, variables.employeeId);
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
